import * as React from 'react'
import { StyleSheet } from 'react-native'
import * as staking from '@mv/api/lib/src/schema/staking'
import { logger } from '../logger'
import { Modal, Heading } from './ModalElements'
import * as Form from '../components/form'
import { RootState } from '../state/state'
import { useDispatch, useSelector } from '../state/store'
import { modalsActions } from '../state/modalsSlice'
import { logEvent, analytics } from '../firebase/analytics'
import { makeCallableFunction, FunctionsError } from '../firebase/functions'
import { ERROR_OOPS, ERROR_USER_NOT_LOGGED_IN } from '../constants/messages'
import { NoRefresh } from '../components/NoRefresh'
import { Text } from '../components'
import { ModalUnstakeData } from '../state/modalsTypes'
import { humanizeBalance } from '../helpers'
import { db, doc, onSnapshot, DocumentReference } from '../firebase/firestore'
import { MyWalletStakingSummaryEarlyUnstakeNotice } from '../components/wallet/MyWalletStakingSummaryEarlyUnstakeNotice'

const MODAL_HEADING = 'Multiverse Staking'

type FormError = {
  amount?: string
  generic?: string
}

export function UnstakeModal(): JSX.Element {
  const isMounted = React.useRef(true)

  const { stakedAI, earlyUnstakingFeeInBasisPoints, earlyUnstakingPeriod } =
    useSelector((state) => state.modals.data) as ModalUnstakeData

  const dispatch = useDispatch()
  const user = useSelector((state) => state.user)

  const [amountFieldValue, setAmountFieldValue] = React.useState('')
  const [formError, setFormError] = React.useState({} as FormError)
  const [unstakeDisabled, setUnstakeDisabled] = React.useState(true)
  const [userStake, setUserStake] = React.useState<
    staking.Stake | null | undefined
  >(null)

  React.useEffect(
    () => () => {
      isMounted.current = false
    },
    []
  )

  React.useEffect(() => {
    if (user.user?.fuid) {
      const userStakeDocRef = doc(
        db,
        staking.documentPath(user.user.fuid)
      ) as DocumentReference<staking.Stake>
      return onSnapshot(userStakeDocRef, (snap) => {
        setUserStake(snap.data())
      })
    }
    return () => {}
  }, [user.user?.fuid])

  React.useEffect(() => {
    const validAmount = checkValidity(amountFieldValue)
    if (validAmount) {
      setUnstakeDisabled(false)
    } else {
      setUnstakeDisabled(true)
    }
  }, [amountFieldValue])

  const validAmount = checkValidity(amountFieldValue)

  return (
    <Modal testID="wallet-stake">
      <Heading>Unstake</Heading>
      <Text weight="bold" style={[styles.available]}>
        Staked AI: {humanizeBalance(userStake?.amount.units || stakedAI, 18)} AI
      </Text>
      <NoRefresh>
        <Form.Item>
          <Form.Label>Amount to Unstake</Form.Label>
          <Form.TextInput
            testID="amountInput"
            value={amountFieldValue}
            onChangeText={(textValue) => {
              setAmountFieldValue(textValue)
            }}
          />
          <Form.Error error={formError.amount} />
        </Form.Item>
        <MyWalletStakingSummaryEarlyUnstakeNotice
          earlyUnstakingFeeInBasisPoints={earlyUnstakingFeeInBasisPoints}
          earlyUnstakingPeriod={earlyUnstakingPeriod}
        />

        <Form.SubmitButton
          testID="submitButton"
          label={validAmount ? `Unstake ${validAmount} AI` : 'Unstake'}
          onPress={async () => {
            setFormError({})
            const result = await submitUnstake(user, validAmount, setFormError)
            if (result) {
              return () => {
                dispatch(
                  modalsActions.showGenericModal({
                    title: MODAL_HEADING,
                    messages: [
                      `Your unstake request of ${validAmount} AI has been received.`,
                    ],
                  })
                )
                logEvent(analytics(), 'unstake', {
                  amount: validAmount,
                  staker: user.user?.fuid,
                })
              }
            }
            return false
          }}
          disabled={unstakeDisabled}
        />
        <Form.Error error={formError.generic} />
      </NoRefresh>
    </Modal>
  )
}
const styles = StyleSheet.create({
  available: {
    fontSize: 16,
    lineHeight: 24,
    marginBottom: 20,
  },
})

async function submitUnstake(
  user: RootState['user'],
  amount: string,
  setFormError: React.Dispatch<React.SetStateAction<FormError>>
): Promise<boolean> {
  if (!user.user) {
    setFormError({
      generic: ERROR_USER_NOT_LOGGED_IN,
    })
    return false
  }

  const unstaking = makeCallableFunction('staking-staking')
  try {
    await unstaking({
      id: user.user.fuid,
      action: 'unstake',
      amount,
    })
    return true
  } catch (_e) {
    const e = _e as FunctionsError
    const errors: FormError = {}

    switch (e.code) {
      case 'invalid-argument':
        logger.warn('stake error', e)
        if (/token|amount|number/i.test(e.message)) {
          errors.amount = 'Invalid amount entered.'
        } else {
          errors.generic = ERROR_OOPS
        }
        break
      case 'failed-precondition':
        logger.warn('stake error', e)
        errors.amount = 'Amount to unstake is larger than your total Stake.'
        break
      default:
        logger.error('stake error', e)
        errors.generic = ERROR_OOPS
    }

    setFormError(errors)
    return false
  }
}

function checkValidity(amountString: string): string {
  let trimmedAmountString = amountString.trim()
  if (!/^[0-9.,]+$/.test(trimmedAmountString)) {
    return ''
  }
  if (trimmedAmountString.includes('.')) {
    if (!/^[0-9,]*\.[0-9]*$/.test(trimmedAmountString)) {
      return ''
    }
  }

  trimmedAmountString = trimmedAmountString
    .replace(/,/g, '') // remove commas
    .replace(/^0+/, '') // remove leading zeroes
    .replace(/^\./, '0.') // add 0 if decimal point at beginning
    .replace(/(\.[0-9]*?)0+$/, '$1') // remove all trailing 0 if decimal point
    .replace(/\.$/, '') // remove decimal point at end

  return trimmedAmountString
}
